import _slicedToArray from "/Users/andrewgallagher/Documents/eth/highrises/static-site/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useReducer } from 'react';
import { useDebouncePair } from '../../../hooks';
import { MultiChainStatesContext } from './context';
import { multicall as multicall1, multicall2, useConfig, useNetwork } from '../../..';
import { useReadonlyNetworks } from '../../network';
import { useBlockNumbers } from '../../blockNumber/blockNumbers';
import { fromEntries } from '../../../helpers/fromEntries';
import { performMulticall } from '../common/performMulticall';
import { callsReducer, chainStateReducer } from '../common';
import { getUniqueCalls } from '../../../helpers';
import { useDevtoolsReporting } from '../common/useDevtoolsReporting';
import { useChainId } from '../../../hooks/useChainId';

function composeChainState(networks, state, multicallAddresses) {
  return fromEntries(Object.keys(networks).map(function (chainId) {
    return [Number(chainId), {
      value: state[Number(chainId)],
      multicallAddress: multicallAddresses[Number(chainId)]
    }];
  }));
}
/**
 * @internal Intended for internal use - use it on your own risk
 */


export function MultiChainStateProvider(_ref) {
  var children = _ref.children,
      multicallAddresses = _ref.multicallAddresses;

  var _useConfig = useConfig(),
      multicallVersion = _useConfig.multicallVersion;

  var networks = useReadonlyNetworks();
  var blockNumbers = useBlockNumbers();

  var _useNetwork = useNetwork(),
      reportError = _useNetwork.reportError;

  var _useReducer = useReducer(callsReducer, []),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      calls = _useReducer2[0],
      dispatchCalls = _useReducer2[1];

  var _useReducer3 = useReducer(chainStateReducer, {}),
      _useReducer4 = _slicedToArray(_useReducer3, 2),
      state = _useReducer4[0],
      dispatchState = _useReducer4[1];

  var multicall = multicallVersion === 1 ? multicall1 : multicall2;

  var _useDebouncePair = useDebouncePair(calls, networks, 50),
      _useDebouncePair2 = _slicedToArray(_useDebouncePair, 2),
      debouncedCalls = _useDebouncePair2[0],
      debouncedNetworks = _useDebouncePair2[1];

  var uniqueCalls = useMemo(function () {
    return getUniqueCalls(debouncedCalls);
  }, [debouncedCalls]); // used for deep equality in hook dependencies

  var uniqueCallsJSON = JSON.stringify(debouncedCalls);
  var chainId = useChainId();
  useDevtoolsReporting(uniqueCallsJSON, uniqueCalls, chainId !== undefined ? blockNumbers[chainId] : undefined, multicallAddresses);

  function multicallForChain(chainId, provider) {
    var blockNumber = blockNumbers[chainId];
    var multicallAddress = multicallAddresses[chainId];

    if (!provider || !blockNumber) {
      return;
    }

    if (!multicallAddress) {
      reportError(new Error("Missing multicall address for chain id ".concat(chainId)));
      return;
    }

    if (debouncedNetworks !== networks) {
      // Wait for debounce to catch up.
      return;
    }

    var callsOnThisChain = uniqueCalls.filter(function (call) {
      return call.chainId === chainId;
    });

    if (callsOnThisChain.length === 0) {
      return;
    }

    performMulticall(provider, multicall, multicallAddress, blockNumber, callsOnThisChain, dispatchState, chainId, reportError);
  }

  useEffect(function () {
    for (var _i = 0, _Object$entries = Object.entries(networks); _i < _Object$entries.length; _i++) {
      var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
          _chainId = _Object$entries$_i[0],
          provider = _Object$entries$_i[1];

      multicallForChain(Number(_chainId), provider);
    }
  }, [blockNumbers, networks, multicallAddresses, uniqueCallsJSON]);
  var chains = useMemo(function () {
    return composeChainState(networks, state, multicallAddresses);
  }, [state, multicallAddresses, networks]);
  var provided = {
    chains: chains,
    dispatchCalls: dispatchCalls
  };
  return _jsx(MultiChainStatesContext.Provider, {
    value: provided,
    children: children
  });
}